import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Menu, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { a11y } from '../lib/utils/accessibility';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);

  // Handle focus trap for mobile menu
  React.useEffect(() => {
    if (isMenuOpen && mobileMenuRef.current) {
      return a11y.focusTrap(mobileMenuRef.current);
    }
  }, [isMenuOpen]);

  return (
    <header className="w-full bg-dark-card border-b border-dark-border sticky top-0 z-50" role="banner">
      <a11y.SkipLink />
      <nav className="max-w-7xl mx-auto px-6 py-4" role="navigation" aria-label="Main">
        <div className="flex items-center justify-between">
          <motion.a
            href="/"
            className="flex items-center space-x-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <DollarSign className="h-8 w-8 text-primary-400" aria-hidden="true" />
            <span className="text-2xl font-bold text-dark-text">Payo AI</span>
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <NavLink href="#features">Features</NavLink>
            <NavLink href="#pricing">Pricing</NavLink>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <motion.a
              href="https://app.payoai.io/login"
              className="px-4 py-2 text-primary-400 hover:text-primary-300 font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Log in
            </motion.a>
            <motion.a
              href="https://buy.stripe.com/5kA5mX5Pv3ov3Ti4gg"
              className="px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Free Trial
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-dark-text" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle menu"
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              id="mobile-menu"
              className="md:hidden mt-4 py-4 border-t border-dark-border"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              role="menu"
              aria-label="Mobile menu"
            >
              <div className="flex flex-col space-y-4">
                <NavLink href="#features">Features</NavLink>
                <NavLink href="#pricing">Pricing</NavLink>
                <div className="pt-4 border-t border-dark-border">
                  <a 
                    href="https://app.payoai.io/login"
                    className="block w-full px-4 py-2 text-primary-400 hover:text-primary-300 font-medium mb-2"
                    role="menuitem"
                  >
                    Log in
                  </a>
                  <a 
                    href="https://buy.stripe.com/5kA5mX5Pv3ov3Ti4gg"
                    className="block w-full px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors"
                    role="menuitem"
                  >
                    Start Free Trial
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <motion.a
      href={href}
      className={cn('text-dark-muted hover:text-dark-text transition-colors', 'font-medium')}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      role="menuitem"
    >
      {children}
    </motion.a>
  );
}